"use client";

import { useId, useState, type ReactNode } from "react";
import { setShallowSearchParams, useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { AreaChevron } from "./area-a-parts";
import {
  AREA_BOARD_CAP_DESKTOP,
  AREA_BOARD_CAP_PHONE,
  areaBoardCss,
  boardVisibleRows,
  parseAreaAbility,
  type AreaAbility,
  type AreaBoardManifestRow,
} from "./area-g3-format";

/**
 * The ranked board's controls (client island): section chips (`?section=`), the surf ability
 * select (`?difficulty=`, which the hero's verdict and best card follow too, AreaAbilitySwitch)
 * and "Show all". The rows
 * are server-rendered children; filtering and the 8 (phone) / 10 (desktop) cap hide rows with a
 * scoped style rule, so the HTML always holds every row in true rank order and the first render
 * already carries the default cap (no jump on hydration). Ranks stay area-wide when filtered.
 */

export type AreaBoardGroup = Readonly<{ key: string; name: string; count: number }>;

const ABILITY_OPTIONS: readonly Readonly<{ value: AreaAbility; label: string }>[] = [
  { value: "all", label: "All levels" },
  { value: "beginner", label: "Beginner friendly" },
  { value: "experienced", label: "Experienced only" },
];

function chipClass(on: boolean): string {
  return `inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-full border px-3.5 text-[14px] font-semibold transition-colors ${
    on ? "border-navy bg-navy text-white" : "border-border bg-white text-navy hover:border-navy"
  }`;
}

function countClass(on: boolean): string {
  return `inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11.5px] font-bold ${on ? "bg-[rgba(91,192,206,0.22)] text-white" : "bg-[#EEF1EA] text-[#454C5A]"}`;
}

function AbilitySelect({ id, value, dark }: Readonly<{ id: string; value: AreaAbility; dark: boolean }>) {
  return (
    <span className="relative inline-flex">
      <select
        id={id}
        value={value}
        onChange={(event) => {
          const next = parseAreaAbility(event.target.value);
          setShallowSearchParams({ difficulty: next === "all" ? null : next });
        }}
        className={`min-h-11 w-[210px] cursor-pointer appearance-none rounded-[10px] border bg-white py-0 pl-3 pr-8 text-[14.5px] font-semibold text-navy ${dark ? "border-white" : "border-[#848E86] hover:border-navy"}`}
      >
        {ABILITY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <span aria-hidden="true" className="pointer-events-none absolute right-[11px] top-[14px] flex text-navy">
        <AreaChevron />
      </span>
    </span>
  );
}

export function AreaBoardControls({
  scope,
  header,
  columnsHead,
  footerLink,
  after,
  groups,
  groupLabel,
  allLabel,
  unitPlural,
  manifest,
  showAbility,
  abilityNote = null,
  tone = "navy",
  children,
}: Readonly<{
  scope: string;
  /** The header's icon, title and sub line (server-rendered). */
  header: ReactNode;
  /**
   * "navy": the header is a navy band with the desktop ability select in it. "light" (the G4
   * week board): a white header, and the ability select sits under the chips at every size.
   */
  tone?: "navy" | "light";
  /** The desktop column head row. */
  columnsHead?: ReactNode;
  /** "Compare with every surf spot worldwide". */
  footerLink?: ReactNode;
  /** Below the footer: the collapsed closed and unscored groups. */
  after?: ReactNode;
  groups: readonly AreaBoardGroup[];
  /** "Coast" or "Area": names the chip row. */
  groupLabel: string;
  /** "All beaches". */
  allLabel: string;
  /** "beaches". */
  unitPlural: string;
  manifest: readonly AreaBoardManifestRow[];
  showAbility: boolean;
  /** Where no ability select applies (the light board, a water sport outside the surf family): one muted line in its place. */
  abilityNote?: string | null;
  children: ReactNode;
}>) {
  const phoneSelectId = useId();
  const deskSelectId = useId();
  const [expanded, setExpanded] = useState(false);
  const rawAbility = useShallowSearchParam("difficulty");
  const ability = showAbility ? parseAreaAbility(rawAbility) : "all";
  const rawGroup = useShallowSearchParam("section");
  const group = rawGroup && groups.some((entry) => entry.key === rawGroup) ? rawGroup : null;
  const visible = boardVisibleRows(manifest, ability, group);
  const css = areaBoardCss({ scope, manifest, ability, group, expanded });
  const filtered = visible.length < manifest.length;
  const moreClass = visible.length > AREA_BOARD_CAP_DESKTOP ? "flex" : "flex md:hidden";
  const groupName = group ? groups.find((entry) => entry.key === group)?.name ?? null : null;
  const total = manifest.length;
  return (
    <div data-area-board={scope}>
      {css && <style>{css}</style>}
      {tone === "navy" ? (
        <div data-area-dark="" className="flex items-center gap-3 bg-navy p-4 text-white md:gap-3.5 md:px-[22px] md:py-[18px]">
          {header}
          {showAbility && (
            <div className="hidden shrink-0 items-center gap-2.5 md:flex">
              <label htmlFor={deskSelectId} className="text-[14px] font-semibold text-[#D5DCE6]">Ability</label>
              <AbilitySelect id={deskSelectId} value={ability} dark />
            </div>
          )}
        </div>
      ) : (
        header
      )}
      {groups.length > 1 && (
        <div
          role="group"
          aria-label={`Filter by ${groupLabel.toLowerCase()}`}
          className="flex gap-1.5 overflow-x-auto px-3 pb-1 pt-3 [mask-image:linear-gradient(90deg,#000_88%,transparent)] [scrollbar-width:none] md:flex-wrap md:overflow-visible md:px-[18px] md:pb-1.5 md:pt-3.5 md:[mask-image:none] [&::-webkit-scrollbar]:hidden"
        >
          <button
            type="button"
            aria-pressed={group === null}
            onClick={() => { setShallowSearchParams({ section: null }); setExpanded(false); }}
            className={chipClass(group === null)}
          >
            <span>{allLabel}</span>
            <span className={countClass(group === null)}>{total}</span>
          </button>
          {groups.map((entry) => (
            <button
              key={entry.key}
              type="button"
              aria-pressed={group === entry.key}
              onClick={() => { setShallowSearchParams({ section: entry.key }); setExpanded(false); }}
              className={chipClass(group === entry.key)}
            >
              <span>{entry.name}</span>
              <span className={countClass(group === entry.key)}>{entry.count}</span>
            </button>
          ))}
          <span aria-hidden="true" className="w-6 shrink-0 md:hidden" />
        </div>
      )}
      {showAbility && (
        <div className={`flex items-center justify-between gap-2.5 px-3 pb-2 pt-1.5 ${tone === "navy" ? "md:hidden" : "md:justify-start md:gap-3 md:px-[18px] md:pb-2.5 md:pt-2"}`}>
          <label htmlFor={phoneSelectId} className="text-[13.5px] font-semibold text-navy md:text-[14px]">Ability</label>
          <AbilitySelect id={phoneSelectId} value={ability} dark={false} />
        </div>
      )}
      {!showAbility && abilityNote && tone === "light" && (
        <p className="px-4 pb-2 pt-1.5 text-[13px] leading-[1.35] text-[#566072] md:px-[22px] md:pb-2.5 md:pt-2">{abilityNote}</p>
      )}
      <p className="sr-only" aria-live="polite">
        {`${visible.length} ${visible.length === 1 ? "row" : unitPlural}${groupName ? ` in ${groupName}` : ""}, in rank order`}
      </p>
      {columnsHead}
      {visible.length === 0 && manifest.length > 0 && (
        <p role="status" className="mx-4 my-2 rounded-[12px] bg-warm-bg p-3.5 text-[14px] text-[#566072] md:mx-[22px]">
          No {unitPlural} match these filters today.{" "}
          <button
            type="button"
            onClick={() => setShallowSearchParams({ difficulty: null, section: null })}
            className="min-h-11 cursor-pointer font-semibold text-teal-dark underline"
          >
            Show every one
          </button>
        </p>
      )}
      {children}
      {filtered && visible.length > 0 && (
        <p className="mx-4 mt-1.5 text-[12.5px] text-[#566072] md:mx-[22px] md:text-[13px]">
          Showing {visible.length} of {manifest.length}. Ranks stay area-wide.
        </p>
      )}
      <div className="flex flex-col gap-2 px-3 pb-3.5 pt-3 md:flex-row md:items-center md:gap-4 md:px-[18px] md:pb-[18px] md:pt-3.5">
        {visible.length > AREA_BOARD_CAP_PHONE && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className={`${moreClass} min-h-12 w-full cursor-pointer items-center justify-between gap-2.5 rounded-[12px] border border-[#BFDDE1] bg-[#F2F8F8] px-4 text-[15px] font-bold text-teal-dark md:w-auto md:min-w-[260px]`}
        >
          {expanded ? "Show fewer" : `Show all ${visible.length} ${unitPlural}`}
          <AreaChevron direction={expanded ? "up" : "down"} />
        </button>
        )}
        {footerLink}
      </div>
      {after}
    </div>
  );
}
